import React, { useMemo } from "react";
import styled from "@emotion/styled";
import moment from "moment";
import Paper from "@mui/material/Paper";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const Container = styled(Paper)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: white;
  width: 250px;
  padding-top: 8px;
  padding-bottom: 8px;
`;

const DayContainer = styled.div`
  padding-right: 10px;
  padding-left: 10px;
  width: 175px;
  align-items: center;
`;

const DayLabel = styled.div`
  font-size: 15px;
  text-align: center;
`;

const IconContainer = styled.div<{
  visibility: "hidden" | "unset";
  disabled: boolean;
}>`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  visibility: ${(props: { visibility: "hidden" | "unset" }) =>
    props.visibility};
  cursor: ${(props: { disabled: boolean }) =>
    props.disabled ? "not-allowed" : "pointer"};
`;

const ArrowBack = styled(ArrowBackIosNewIcon)`
  width: auto;
  height: 13px;
`;

const ArrowForward = styled(ArrowForwardIosIcon)`
  width: auto;
  height: 13px;
`;

type Props = {
  disabled: boolean;
  selectedTimestamp: number;
  maxDaysAllowed: number;
  onDaysSelected: Function;
};

function DaysSlider({
  onDaysSelected,
  disabled,
  selectedTimestamp,
  maxDaysAllowed,
}: Props): JSX.Element {
  const now = useMemo(() => {
    return moment(selectedTimestamp * 1000);
  }, [selectedTimestamp]);

  const displayPreviousArrow = useMemo(() => {
    const diffDays = moment().startOf("day").diff(now, "days");

    if (diffDays >= maxDaysAllowed) {
      return "hidden";
    }
    if (
      diffDays === maxDaysAllowed - 1 &&
      now.isSame(moment().subtract(maxDaysAllowed, "d"), "day")
    ) {
      return "hidden";
    }

    return "unset";
  }, [now, maxDaysAllowed]);

  const displayNextArrow = useMemo(() => {
    const diffDays = moment(new Date()).diff(now, "days");

    if (diffDays <= 0 && now.isSame(new Date(), "day")) {
      return "hidden";
    }
    return "unset";
  }, [now]);

  function handleNextDay() {
    if (disabled) return null;

    const newTimestamp = moment(selectedTimestamp * 1000)
      .add(1, "d")
      .unix();

    return onDaysSelected(newTimestamp);
  }

  function handlePreviousDay() {
    if (disabled) return null;

    const newTimestamp = moment(selectedTimestamp * 1000)
      .subtract(1, "d")
      .unix();
    return onDaysSelected(newTimestamp);
  }

  return (
    <Container>
      <IconContainer
        visibility={displayPreviousArrow}
        onClick={handlePreviousDay}
        // @ts-ignore
        disabled={disabled}
      >
        <ArrowBack />
      </IconContainer>

      <DayContainer>
        <DayLabel>{now.format("dddd D MMMM")}</DayLabel>
      </DayContainer>
      <IconContainer
        visibility={displayNextArrow}
        onClick={handleNextDay}
        // @ts-ignore
        disabled={disabled}
      >
        <ArrowForward />
      </IconContainer>
    </Container>
  );
}

export default DaysSlider;
